import React, { useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { DATE_FORMAT, DATE_TIME_FORMAT, toDate } from "../../../Utils/dateFormat";

const SIZE_MAP = { sm: "small", lg: "large" };

const toDayjs = (value) => {
  const d = toDate(value);
  return d ? dayjs(d) : null;
};

/**
 * Drop-in replacement for <input type="date"> / <input type="datetime-local">.
 *
 *  - SHOWS the date as   Feb 02, 1984   (or  Feb 02, 1984 01:45 PM  with `withTime`)
 *  - still hands the parent an event-like object whose target.value is the ISO value the
 *    API expects:  "1984-02-02"  /  "1984-02-02T13:45"
 *
 * so existing handlers such as  handleChange(e) { const { name, value } = e.target }  keep working.
 */
export default function FormattedDateInput({
  name,
  value,
  onChange,
  min,
  max,
  disabled,
  size,
  className,
  placeholder,
  withTime = false,
}) {
  const [inner, setInner] = useState(value ?? "");
  const controlled = value !== undefined;
  const current = controlled ? value : inner;

  const minDay = min ? toDayjs(min) : null;
  const maxDay = max ? toDayjs(max) : null;

  const disabledDate = (day) => {
    if (!day) return false;
    if (minDay && day.isBefore(minDay.startOf("day"))) return true;
    if (maxDay && day.isAfter(maxDay.endOf("day"))) return true;
    return false;
  };

  const handleChange = (date) => {
    const next = date ? date.format(withTime ? "YYYY-MM-DDTHH:mm" : "YYYY-MM-DD") : "";
    if (!controlled) setInner(next);
    if (onChange) {
      const target = { name, value: next, type: withTime ? "datetime-local" : "date" };
      onChange({
        target,
        currentTarget: target,
        persist: () => {},
        preventDefault: () => {},
        stopPropagation: () => {},
      });
    }
  };

  return (
    <DatePicker
      name={name}
      value={toDayjs(current)}
      onChange={handleChange}
      format={withTime ? DATE_TIME_FORMAT : DATE_FORMAT}
      showTime={withTime ? { use12Hours: true, format: "hh:mm A" } : false}
      use12Hours={withTime}
      disabledDate={disabledDate}
      disabled={disabled}
      size={SIZE_MAP[size]}
      className={className}
      placeholder={placeholder || (withTime ? "Select date & time" : "Select date")}
      allowClear
      style={{ width: "100%" }}
      popupStyle={{ zIndex: 2100 }}
    />
  );
}
